import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me')
};

// Centers APIs
export const centersAPI = {
  getAll: (includeInactive = false) => api.get('/centers', { params: { include_inactive: includeInactive } }),
  getOne: (id) => api.get(`/centers/${id}`),
  create: (data) => api.post('/centers', data),
  update: (id, data) => api.put(`/centers/${id}`, data),
  delete: (id) => api.delete(`/centers/${id}`)
};

// Service Types APIs
export const serviceTypesAPI = {
  getAll: (includeInactive = false) => api.get('/service-types', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/service-types', data),
  update: (id, data) => api.put(`/service-types/${id}`, data),
  delete: (id) => api.delete(`/service-types/${id}`)
};

// Services APIs
export const servicesAPI = {
  getAll: (serviceTypeId, includeInactive = false) => {
    const params = { include_inactive: includeInactive };
    if (serviceTypeId) params.service_type_id = serviceTypeId;
    return api.get('/services', { params });
  },
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`)
};

// Sub-Services APIs
export const subServicesAPI = {
  getAll: (serviceId, includeInactive = false) => {
    const params = { include_inactive: includeInactive };
    if (serviceId) params.service_id = serviceId;
    return api.get('/sub-services', { params });
  },
  create: (data) => api.post('/sub-services', data),
  update: (id, data) => api.put(`/sub-services/${id}`, data),
  delete: (id) => api.delete(`/sub-services/${id}`)
};

// Nationalities APIs
export const nationalitiesAPI = {
  getAll: (includeInactive = false) =>
    api.get('/nationalities', { params: { include_inactive: includeInactive } }),

  create: (data) =>
    api.post('/nationalities', data),

  delete: (id) =>
    api.delete(`/nationalities/${id}`),

  // ✅ NEW: Default nationality
  getDefault: () =>
    api.get('/nationalities/default'),

  setDefault: (id) =>
    api.post(`/nationalities/${id}/set-default`)
};

// Fee Structure APIs
export const feesAPI = {
  getAll: (filters = {}) => api.get('/fees', { params: filters }),
  create: (data) => api.post('/fees', data),
  update: (id, data) => api.put(`/fees/${id}`, data),
  delete: (id) => api.delete(`/fees/${id}`),
  calculate: (nationalityId, serviceTypeId, serviceId, subServiceId) => 
    api.get('/calculate-fee', { 
      params: { 
        nationality_id: nationalityId, 
        service_type_id: serviceTypeId, 
        service_id: serviceId,
        sub_service_id: subServiceId
      } 
    })
};

// Appointment Configuration APIs
export const appointmentConfigAPI = {
  getAll: (centerId) => api.get('/appointment-config', { params: centerId ? { center_id: centerId } : {} }),
  create: (data) => api.post('/appointment-config', data),
  update: (id, data) => api.put(`/appointment-config/${id}`, data),
  delete: (id) => api.delete(`/appointment-config/${id}`)
};

// News & Media APIs
export const newsAPI = {
  getAll: (filters = {}) => api.get('/news', { params: filters }),
  getOne: (id) => api.get(`/news/${id}`),
  create: (data) => api.post('/news', data),
  update: (id, data) => api.put(`/news/${id}`, data),
  delete: (id) => api.delete(`/news/${id}`)
};

// Testimonials APIs
export const testimonialsAPI = {
  getAll: (filters = {}) => api.get('/testimonials', { params: filters }),
  create: (data) => api.post('/testimonials', data),
  update: (id, data) => api.put(`/testimonials/${id}`, data),
  delete: (id) => api.delete(`/testimonials/${id}`)
};

// OTP APIs
export const otpAPI = {
  send: (email) => api.post('/otp/send', { email }),
  verify: (data) => api.post('/otp/verify', data)
};

// Time Slots APIs
export const timeSlotsAPI = {
  getAvailable: (centerId, date) => 
    api.get('/time-slots', { params: { center_id: centerId, appointment_date: date } })
};

// Appointments APIs
export const appointmentsAPI = {
  create: (data) => api.post('/appointments', data),
  getAll: (filters) => api.get('/appointments', { params: filters }),
  update: (id, data) => api.patch(`/appointments/${id}`, data),
  cancel: (id) => api.patch(`/appointments/${id}/cancel`),
  getPdf: (id) => `${API_URL}/appointments/${id}/pdf`
};

// Grievances APIs
export const grievancesAPI = {
  create: (data) => api.post('/grievances', data),
  getAll: (filters) => api.get('/grievances', { params: filters }),
  update: (id, data) => api.patch(`/grievances/${id}`, data)
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats')
};

export default api;

// Single Service API
export const serviceDetailAPI = {
  getOne: (id) => api.get(`/services/${id}`)
};
